// js-beautify is ~200 KB and only needed by "Format document". It is imported
// dynamically so it never lands in the main bundle — validation and safety
// checks below have no third-party dependency at all.

/**
 * HTML analysis for the Communication Template editors.
 *
 * Three concerns, deliberately separate:
 *
 *  1. `validateHtml`    — structural correctness. Blocks Save.
 *  2. `checkEmailSafety`— email-client compatibility. Blocks Publish for
 *                         dangerous tags, warns for unsupported ones.
 *  3. `isVisualEditorSafe` — whether TipTap can round-trip the document without
 *                         destroying it. Guards the Visual tab.
 *
 * Nothing here rewrites the author's HTML. Detection only — silent repair is
 * explicitly out of scope, because a template that is quietly "fixed" is a
 * template nobody can debug.
 */

export type IssueSeverity = 'error' | 'warning';

export interface HtmlIssue {
  severity: IssueSeverity;
  code: string;
  message: string;
  /** 1-based line in the source, when it can be located. */
  line?: number;
  detail?: string;
}

// ── Variable protection ──────────────────────────────────────────────────────

/**
 * `{{variable}}` tokens must survive every transform untouched — never escaped,
 * never brace-encoded. Formatting runs on placeholder-substituted text so the
 * beautifier can never split a token across lines or entity-encode the braces.
 */
const VAR_PATTERN = /\{\{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*\}\}/g;

/**
 * Sentinel is plain word characters on purpose: the beautifier treats it as an
 * ordinary text run, so it can never be split across lines, indented into, or
 * entity-encoded the way raw {{ }} braces can be.
 */
const VAR_SENTINEL_PREFIX = 'CTVARx';
const VAR_SENTINEL_SUFFIX = 'xENDCTV';
const SENTINEL_PATTERN = new RegExp(`${VAR_SENTINEL_PREFIX}(\\d+)${VAR_SENTINEL_SUFFIX}`, 'g');

function protectVariables(html: string): { text: string; tokens: string[] } {
  const tokens: string[] = [];
  const text = html.replace(VAR_PATTERN, (match) => {
    tokens.push(match);
    return VAR_SENTINEL_PREFIX + (tokens.length - 1) + VAR_SENTINEL_SUFFIX;
  });
  return { text, tokens };
}

function restoreVariables(text: string, tokens: string[]): string {
  return text.replace(SENTINEL_PATTERN, (whole, i: string) => tokens[Number(i)] ?? whole);
}

/** Extracts the distinct `{{variable}}` keys used in a document. */
export function extractVariables(html: string): string[] {
  const keys = (html.match(VAR_PATTERN) ?? []).map((t) => t.replace(/[{}\s]/g, ''));
  return [...new Set(keys)].sort();
}

// ── Formatting ───────────────────────────────────────────────────────────────

/** Pretty-prints HTML. Variables are protected across the transform. */
export async function formatHtml(html: string): Promise<string> {
  const { html_beautify } = await import('js-beautify');
  const { text, tokens } = protectVariables(html);
  const formatted = html_beautify(text, {
    indent_size: 2,
    wrap_line_length: 0, // never hard-wrap — long inline styles must stay intact
    preserve_newlines: true,
    max_preserve_newlines: 2,
    indent_inner_html: true,
    unformatted: ['code', 'pre'],
    extra_liners: [],
  });
  return restoreVariables(formatted, tokens);
}

// ── Structural validation ────────────────────────────────────────────────────

/** Void elements that legitimately never close. */
const VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

/** Tags whose end tag may be omitted per the HTML spec — not an error. */
const OPTIONAL_CLOSE = new Set(['li', 'td', 'th', 'tr', 'thead', 'tbody', 'tfoot', 'p', 'option']);

const TAG_PATTERN = /<\/?([a-zA-Z][a-zA-Z0-9-]*)\b[^>]*?(\/?)>/g;

function lineOf(source: string, index: number): number {
  return source.slice(0, index).split('\n').length;
}

/**
 * Structural HTML validation: unclosed tags, mismatched nesting, broken tables,
 * invalid images.
 *
 * Uses an explicit tag stack rather than DOMParser, because the browser silently
 * repairs bad markup — which would hide exactly the errors the author needs to
 * see.
 */
export function validateHtml(html: string): HtmlIssue[] {
  const issues: HtmlIssue[] = [];
  if (!html.trim()) {
    return [{ severity: 'error', code: 'EMPTY_HTML', message: 'HTML body is empty.' }];
  }

  // Strip comments and the contents of raw-text elements so their bodies are
  // never parsed as markup.
  const scrubbed = html
    .replace(/<!--[\s\S]*?-->/g, (m) => ' '.repeat(m.length))
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, (m) => ' '.repeat(m.length));

  const stack: { tag: string; index: number }[] = [];
  let match: RegExpExecArray | null;

  TAG_PATTERN.lastIndex = 0;
  while ((match = TAG_PATTERN.exec(scrubbed)) !== null) {
    const raw = match[0];
    const tag = (match[1] ?? '').toLowerCase();
    const selfClosed = match[2] === '/';
    const isClosing = raw.startsWith('</');

    if (VOID_TAGS.has(tag) || selfClosed) continue;

    if (!isClosing) {
      stack.push({ tag, index: match.index });
      continue;
    }

    // Closing tag — find its partner.
    const openIdx = [...stack].reverse().findIndex((e) => e.tag === tag);
    if (openIdx === -1) {
      issues.push({
        severity: 'error',
        code: 'UNEXPECTED_CLOSING_TAG',
        message: `Closing </${tag}> has no matching opening tag.`,
        line: lineOf(scrubbed, match.index),
      });
      continue;
    }

    const realIdx = stack.length - 1 - openIdx;
    // Anything left open above the partner is mis-nested.
    for (let i = stack.length - 1; i > realIdx; i--) {
      const orphan = stack[i]!;
      if (!OPTIONAL_CLOSE.has(orphan.tag)) {
        issues.push({
          severity: 'error',
          code: 'INVALID_NESTING',
          message: `<${orphan.tag}> is not closed before </${tag}>.`,
          line: lineOf(scrubbed, orphan.index),
        });
      }
    }
    stack.length = realIdx;
  }

  for (const unclosed of stack) {
    if (OPTIONAL_CLOSE.has(unclosed.tag)) continue;
    issues.push({
      severity: 'error',
      code: 'UNCLOSED_TAG',
      message: `<${unclosed.tag}> is never closed.`,
      line: lineOf(scrubbed, unclosed.index),
    });
  }

  issues.push(...validateTables(scrubbed));
  issues.push(...validateImages(html));

  return issues;
}

/** Email layouts live and die by tables — check their structure specifically. */
function validateTables(html: string): HtmlIssue[] {
  const issues: HtmlIssue[] = [];

  const openTables = (html.match(/<table\b/gi) ?? []).length;
  const closeTables = (html.match(/<\/table>/gi) ?? []).length;
  if (openTables !== closeTables) {
    issues.push({
      severity: 'error',
      code: 'BROKEN_TABLE',
      message: `Unbalanced <table> tags — ${openTables} opened, ${closeTables} closed.`,
    });
  }

  // Containment must be tracked by DEPTH, not by "where was the last <table>".
  //
  // A lastIndexOf-based check is wrong for nested tables — which every real
  // email layout uses. Given:
  //
  //   <table>            <- outer opens
  //     <tr><td><table>…</table></td></tr>
  //     <tr>             <- lastIndexOf('</table>') is now AFTER
  //                         lastIndexOf('<table'), so a naive check concludes
  //                         this row is outside a table and rejects it.
  //
  // A single flat table has no inner </table> before its rows, which is exactly
  // why simple templates passed while real ones were rejected.
  let tableDepth = 0;
  let rowDepth = 0;
  let reportedCell = false;
  let reportedRow = false;

  const STRUCT_PATTERN = /<(\/?)(table|tr|td|th)\b[^>]*?(\/?)>/gi;
  let m: RegExpExecArray | null;
  STRUCT_PATTERN.lastIndex = 0;

  while ((m = STRUCT_PATTERN.exec(html)) !== null) {
    const closing = m[1] === '/';
    const tag = (m[2] ?? '').toLowerCase();
    const selfClosed = m[3] === '/';
    if (selfClosed) continue;

    if (tag === 'table') {
      if (closing) {
        tableDepth = Math.max(0, tableDepth - 1);
        // Leaving a table also leaves any row left open inside it.
        rowDepth = 0;
      } else {
        tableDepth += 1;
        // A nested table starts a fresh row context of its own.
        rowDepth = 0;
      }
      continue;
    }

    if (tag === 'tr') {
      if (closing) {
        rowDepth = Math.max(0, rowDepth - 1);
      } else {
        if (tableDepth === 0 && !reportedRow) {
          issues.push({
            severity: 'error',
            code: 'ROW_OUTSIDE_TABLE',
            message: 'A <tr> appears outside a <table>.',
            line: lineOf(html, m.index),
          });
          reportedRow = true;
        }
        // <tr> end tags are optional, so a new row implicitly closes the last.
        rowDepth = 1;
      }
      continue;
    }

    // td / th
    if (!closing && rowDepth === 0 && !reportedCell) {
      issues.push({
        severity: 'error',
        code: 'CELL_OUTSIDE_ROW',
        message: 'A <td>/<th> appears outside a <tr>.',
        line: lineOf(html, m.index),
      });
      reportedCell = true;
    }
  }

  // colspan/rowspan must be positive integers or Outlook drops the cell.
  const spanPattern = /\b(colspan|rowspan)\s*=\s*["']?([^"'\s>]*)/gi;
  while ((m = spanPattern.exec(html)) !== null) {
    const value = m[2] ?? '';
    if (!/^\d+$/.test(value) || Number(value) < 1) {
      issues.push({
        severity: 'error',
        code: 'INVALID_SPAN',
        message: `${m[1]}="${value}" must be a positive whole number.`,
        line: lineOf(html, m.index),
      });
    }
  }

  return issues;
}

function validateImages(html: string): HtmlIssue[] {
  const issues: HtmlIssue[] = [];
  const imgPattern = /<img\b[^>]*>/gi;
  let m: RegExpExecArray | null;

  while ((m = imgPattern.exec(html)) !== null) {
    const tag = m[0];
    const src = /\bsrc\s*=\s*["']([^"']*)["']/i.exec(tag)?.[1];

    if (src === undefined) {
      issues.push({
        severity: 'error',
        code: 'IMAGE_NO_SRC',
        message: '<img> is missing a src attribute.',
        line: lineOf(html, m.index),
      });
    } else if (!src.trim()) {
      issues.push({
        severity: 'error',
        code: 'IMAGE_EMPTY_SRC',
        message: '<img> has an empty src.',
        line: lineOf(html, m.index),
      });
    }

    if (!/\balt\s*=/i.test(tag)) {
      issues.push({
        severity: 'warning',
        code: 'IMAGE_NO_ALT',
        message: '<img> has no alt text — many clients block images by default.',
        line: lineOf(html, m.index),
      });
    }
  }

  return issues;
}

// ── Email-client safety ──────────────────────────────────────────────────────

/**
 * Rejected outright — script-capable or actively dangerous in an email.
 *
 * `meta` is deliberately NOT a blanket ban. A production email template is a
 * full HTML document, and `<meta charset>` / `<meta name="viewport">` are
 * required for correct rendering in Gmail and Apple Mail. Only the redirecting
 * form is dangerous, handled separately below.
 */
const DANGEROUS_TAGS = ['script', 'iframe', 'object', 'embed', 'form', 'base'] as const;

/** Allowed to save, but will not render in most email clients. */
const UNSUPPORTED_TAGS = ['video', 'audio', 'canvas', 'svg', 'input', 'button', 'select'] as const;

/**
 * Email-client safety. Dangerous constructs are errors and block Publish;
 * unsupported ones are warnings so the author can decide.
 */
export function checkEmailSafety(html: string): HtmlIssue[] {
  const issues: HtmlIssue[] = [];

  for (const tag of DANGEROUS_TAGS) {
    const pattern = new RegExp(`<${tag}\\b`, 'i');
    const idx = html.search(pattern);
    if (idx !== -1) {
      issues.push({
        severity: 'error',
        code: 'DANGEROUS_TAG',
        message: `<${tag}> is not allowed in an email template.`,
        line: lineOf(html, idx),
        detail:
          tag === 'script'
            ? 'Scripts are stripped by every email client and flag the message as malicious.'
            : `<${tag}> is blocked by email clients and is a common phishing vector.`,
      });
    }
  }

  for (const tag of UNSUPPORTED_TAGS) {
    const pattern = new RegExp(`<${tag}\\b`, 'i');
    const idx = html.search(pattern);
    if (idx !== -1) {
      issues.push({
        severity: 'warning',
        code: 'UNSUPPORTED_TAG',
        message: `<${tag}> is not supported by most email clients.`,
        line: lineOf(html, idx),
        detail: 'It will be ignored or stripped — provide a fallback.',
      });
    }
  }

  // Only the redirecting form of <meta> is a threat — it silently navigates the
  // reader elsewhere and is a classic phishing vector. charset/viewport are
  // required for correct rendering and are explicitly allowed.
  const metaRefresh = /<meta\b[^>]*\bhttp-equiv\s*=\s*["']?\s*refresh/i.exec(html);
  if (metaRefresh) {
    issues.push({
      severity: 'error',
      code: 'META_REFRESH',
      message: '<meta http-equiv="refresh"> is not allowed — it redirects the reader.',
      line: lineOf(html, metaRefresh.index),
    });
  }

  // Inline event handlers (onclick, onload, …) — script execution by another name.
  const handler = /\bon[a-z]+\s*=\s*["']/i.exec(html);
  if (handler) {
    issues.push({
      severity: 'error',
      code: 'INLINE_EVENT_HANDLER',
      message: `Inline event handler ${handler[0].replace(/\s*=.*$/, '')} is not allowed.`,
      line: lineOf(html, handler.index),
    });
  }

  const jsUrl = /(?:href|src)\s*=\s*["']\s*javascript:/i.exec(html);
  if (jsUrl) {
    issues.push({
      severity: 'error',
      code: 'JAVASCRIPT_URL',
      message: 'javascript: URLs are not allowed.',
      line: lineOf(html, jsUrl.index),
    });
  }

  const fixed = /position\s*:\s*fixed/i.exec(html);
  if (fixed) {
    issues.push({
      severity: 'warning',
      code: 'POSITION_FIXED',
      message: 'position:fixed is unsupported in email — use table layout instead.',
      line: lineOf(html, fixed.index),
    });
  }

  const external = /<link\b[^>]*\brel\s*=\s*["']?stylesheet/i.exec(html);
  if (external) {
    issues.push({
      severity: 'warning',
      code: 'EXTERNAL_STYLESHEET',
      message: 'External stylesheets are stripped by email clients — inline your CSS.',
      line: lineOf(html, external.index),
    });
  }

  const styleBlock = /<style\b/i.exec(html);
  if (styleBlock) {
    issues.push({
      severity: 'warning',
      code: 'STYLE_BLOCK',
      message: '<style> blocks are stripped by Gmail — prefer inline style attributes.',
      line: lineOf(html, styleBlock.index),
    });
  }

  return issues;
}

// ── Visual-editor round-trip safety ──────────────────────────────────────────

/**
 * Whether TipTap can represent this document without destroying it.
 *
 * TipTap's configured schema (StarterKit + underline/align/link) has no table,
 * image or style-attribute support. Loading rich email HTML into it and reading
 * it back silently collapses the layout — tables, inline styles and images are
 * simply dropped from the resulting document.
 *
 * The Visual tab uses this to refuse to take ownership of content it would
 * damage, instead of losing the author's work the moment they type.
 */
export function isVisualEditorSafe(html: string): boolean {
  return getVisualEditorRisks(html).length === 0;
}

/** Human-readable list of what the visual editor would drop. */
export function getVisualEditorRisks(html: string): string[] {
  const risks: string[] = [];
  if (/<table\b/i.test(html)) risks.push('table layout');
  if (/<img\b/i.test(html)) risks.push('images');
  if (/\bstyle\s*=\s*["']/i.test(html)) risks.push('inline styles');
  if (/<style\b/i.test(html)) risks.push('<style> blocks');
  if (/<!DOCTYPE|<html\b|<head\b|<body\b/i.test(html)) risks.push('full document structure');
  if (/<(span|div)\b[^>]*\bclass\s*=/i.test(html)) risks.push('CSS classes');
  return risks;
}

// ── Plain text ───────────────────────────────────────────────────────────────

/**
 * Derives a readable plain-text part from HTML.
 *
 * `{{variables}}` are preserved verbatim so the text part stays renderable.
 */
export function htmlToPlainText(html: string): string {
  let text = html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|h[1-6]|li|table)>/gi, '\n')
    .replace(/<\/td>/gi, '  ')
    .replace(/<li\b[^>]*>/gi, '- ')
    .replace(/<[^>]+>/g, '');

  // Decode the entities that actually show up in email bodies.
  const entities: Record<string, string> = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&middot;': '·',
    '&mdash;': '—',
    '&ndash;': '–',
  };
  text = text.replace(/&[a-z#0-9]+;/gi, (e) => entities[e.toLowerCase()] ?? e);

  return text
    .split('\n')
    .map((l) => l.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
